import { safeSessionStorage } from './safeStorage';

const SOURCE_KEY = 'reservation_source';
const UTM_KEY = 'reservation_utm';
const REFERRER_KEY = 'reservation_referrer';

/**
 * Internal Meitre domains that should not count as external referrers.
 * When the user navigates to auth.v2.meitre.com and back, document.referrer
 * points to the auth subdomain — we must ignore that.
 */
function isInternalReferrer(referrer: string): boolean {
  try {
    const host = new URL(referrer).hostname;
    return host.endsWith('.v2.meitre.com')
      || host.endsWith('.meitre.com')
      || host.endsWith('.meitre-test.com')
      || host === 'localhost';
  } catch {
    return false;
  }
}

interface CapturedAttribution {
  source: string;
  utm: Record<string, string>;
  referrer: string;
}

/**
 * Captures reservation source, UTM params, and referrer on first call,
 * then persists them in sessionStorage so they survive auth redirects,
 * SPA navigation, and page reloads.
 *
 * Source priority (same as legacy): utm_source > document.referrer > 'direct'
 */
function captureOnce(): CapturedAttribution {
  const stored = safeSessionStorage.getItem(SOURCE_KEY);
  if (stored) {
    let utm: Record<string, string> = {};
    try { utm = JSON.parse(safeSessionStorage.getItem(UTM_KEY) || '{}'); } catch { /* use empty */ }
    const referrer = safeSessionStorage.getItem(REFERRER_KEY) || '';
    return { source: stored, utm, referrer };
  }

  const params = new URLSearchParams(window.location.search);

  // Referrer (filtered for internal domains)
  const rawReferrer = document.referrer || '';
  const referrer = rawReferrer && !isInternalReferrer(rawReferrer) ? rawReferrer : '';

  // Source
  let source = 'direct';
  const utmSource = params.get('utm_source');
  if (utmSource) {
    source = utmSource;
  } else if (referrer) {
    source = referrer;
  }

  // UTM params (camelCase keys as backend expects)
  const utm: Record<string, string> = {};
  if (utmSource) utm.utmSource = utmSource;
  const utmMedium = params.get('utm_medium');
  if (utmMedium) utm.utmMedium = utmMedium;
  const utmCampaign = params.get('utm_campaign');
  if (utmCampaign) utm.utmCampaign = utmCampaign;

  safeSessionStorage.setItem(SOURCE_KEY, source);
  safeSessionStorage.setItem(UTM_KEY, JSON.stringify(utm));
  safeSessionStorage.setItem(REFERRER_KEY, referrer);

  return { source, utm, referrer };
}

/**
 * Eagerly captures attribution data from the landing URL.
 * Must be called once on app startup (main.tsx), before SPA navigation
 * strips query params and changes document.referrer.
 */
export function initReservationAttribution(): void {
  captureOnce();
}

/**
 * Returns the reservation source, captured on first visit.
 * Survives auth redirects (auth.v2.meitre.com → back) and page reloads.
 */
export function calculateReservationSource(): string {
  return captureOnce().source;
}

/**
 * Returns UTM parameters captured on first visit.
 * Backend expects camelCase field names: utmSource, utmMedium, utmCampaign.
 */
export function getUtmParams(): Record<string, string> {
  return captureOnce().utm;
}

/**
 * Returns the original external referrer, captured on first visit.
 * Internal Meitre domains are filtered out (returns empty string).
 */
export function getCapturedReferrer(): string {
  return captureOnce().referrer;
}
